import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Phone,
  Shield,
  Wrench,
  ClipboardList,
  Calendar,
  CreditCard,
  Battery,
  Sparkles,
  Loader2,
  Info,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EOFYBatteryDealPage = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    postcode: '',
    hasSolar: '',
    package: '',
    preferredContact: 'phone',
    message: '',
  });
  const [status, setStatus] = useState({ loading: false, ok: false, error: '' });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, error: '' });
    try {
      const res = await fetch(`${API_URL}/api/eofy-battery-deal/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus({ loading: false, ok: true, error: '' });
    } catch (err) {
      setStatus({ loading: false, ok: false, error: 'Something went wrong. Please call us on 0478 453 548 or try again.' });
    }
  };

  const ctaScroll = () => {
    document.getElementById('eofy-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="EOFY Battery Deal — eCactus Agave from $3,999 Installed*"
        description="Limited EOFY offer: eCactus Agave 10.24 kWh battery from $3,999 installed (after rebate) or 20.48 kWh from $4,999. Lock in with a $250 site inspection deposit."
      />
      <Header />
      <main>
        {/* HERO */}
        <section
          className="pt-28 pb-12 sm:pb-16 relative overflow-hidden bg-gradient-to-br from-[#f6f8f3] via-white to-[#eaf2dc]"
          data-testid="eofy-hero"
        >
          <div
            className="absolute inset-0 opacity-[0.15] pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          <div className="absolute top-20 right-0 w-72 h-72 bg-[#B5D43B]/20 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-3 py-1 mb-4">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                  <span className="text-[11px] font-bold tracking-wider uppercase text-red-700">
                    Limited EOFY Offer · Ends 30 June 2026
                  </span>
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#3D5A47] leading-[1.1] tracking-tight">
                  EOFY <span className="text-[#B5D43B] bg-[#3D5A47] px-2 rounded-md">Battery Deal</span>
                </h1>
                <p className="text-base sm:text-lg text-gray-700 mt-5 max-w-2xl leading-relaxed">
                  Get an <strong>eCactus Agave</strong> home battery package from just{' '}
                  <strong>$3,999 installed after rebate*</strong>. Lock in your install slot before EOFY with a $250 site inspection deposit.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <Button
                    onClick={ctaScroll}
                    className="bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 text-sm sm:text-base font-semibold px-5 py-5 rounded-xl shadow-lg shadow-[#3D5A47]/20"
                    data-testid="eofy-hero-cta"
                  >
                    Get My Battery Deal <ArrowRight className="w-4 h-4" />
                  </Button>
                  <a href="tel:0478453548">
                    <Button
                      variant="outline"
                      className="bg-white border-[#3D5A47]/20 text-[#3D5A47] hover:bg-[#f8faf5] gap-2 text-sm sm:text-base px-5 py-5 rounded-xl"
                      data-testid="eofy-hero-call"
                    >
                      <Phone className="w-4 h-4" /> Book Site Inspection
                    </Button>
                  </a>
                </div>
                <p className="text-[11px] text-gray-500 mt-4">
                  Standard installation conditions apply. Additional costs may apply.
                </p>
              </div>

              <div className="lg:col-span-5 grid grid-cols-1 gap-3">
                {/* Hero product showcase card */}
                <div className="bg-white rounded-2xl border border-[#B5D43B]/30 p-4 sm:p-5 shadow-md relative overflow-hidden">
                  <div
                    className="absolute inset-0 opacity-[0.12] pointer-events-none"
                    style={{
                      backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)',
                      backgroundSize: '18px 18px',
                    }}
                  />
                  <div className="relative flex items-center gap-4">
                    <img
                      src="/product-images/whes/agave-sh-render.png"
                      alt="eCactus Agave SH all-in-one battery stack"
                      className="h-40 sm:h-48 w-auto object-contain flex-shrink-0"
                      data-testid="eofy-hero-product-image"
                    />
                    <div className="min-w-0">
                      <span className="inline-block text-[10px] uppercase tracking-widest text-[#B5D43B] font-bold mb-1">
                        eCactus Agave SH
                      </span>
                      <p className="text-xs text-gray-500 leading-snug">
                        All-in-one residential battery — hybrid inverter, LFP modules and EMS in one IP65 cabinet.
                      </p>
                    </div>
                  </div>
                </div>

                {[
                  { kwh: '10.24 kWh', price: '$3,999', stacks: '1 × WH-SPHA5.0H stack', val: '10.24' },
                  { kwh: '20.48 kWh', price: '$4,999', stacks: '2 × WH-SPHA5.0H stacks (master + slave)', val: '20.48' },
                ].map((p) => (
                  <div
                    key={p.kwh}
                    className="bg-white rounded-2xl border-2 border-[#B5D43B]/30 p-4 sm:p-5 shadow-md flex items-center gap-4"
                    data-testid={`eofy-price-card-${p.val}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/20 flex items-center justify-center flex-shrink-0">
                      <Battery className="w-6 h-6 text-[#3D5A47]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                        eCactus Agave {p.kwh}
                      </p>
                      <p className="text-2xl sm:text-3xl font-black text-[#3D5A47] leading-tight">
                        From {p.price}<span className="text-sm text-gray-500 font-medium"> installed*</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{p.stacks}</p>
                    </div>
                  </div>
                ))}
                <p className="text-[10px] text-gray-500">*Price after applicable Battery STC rebate is assigned to Supa Energy Hub. Standard install only.</p>
              </div>
            </div>
          </div>
        </section>

        {/* PACKAGE / PRODUCT */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">The Package</span>
            <h2 className="heading-2 text-[#3D5A47] mb-3">eCactus Agave Battery Package</h2>
            <p className="text-gray-600 max-w-3xl">
              Our EOFY special is built around the <strong>eCactus Agave</strong> hybrid inverter + LFP battery stack — model <code className="text-sm bg-[#f8faf5] px-2 py-0.5 rounded">WH-SPHA5.0H-10.24kWh</code>. Each package includes the hybrid inverter and battery stack ready for single-phase install.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {[
                { name: '10.24 kWh Package', cfg: '1 × WH-SPHA5.0H-10.24kWh stack', note: 'Best for homes with moderate evening usage.' },
                { name: '20.48 kWh Package', cfg: '2 × WH-SPHA5.0H-10.24kWh stacks', note: 'One inverter operates as master, the second as slave. Best for larger homes & higher evening usage.' },
              ].map((p) => (
                <div key={p.name} className="bg-[#f8faf5] rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-[#3D5A47]">{p.name}</h3>
                  <p className="text-sm text-gray-700 mt-1"><strong>Configuration:</strong> {p.cfg}</p>
                  <p className="text-sm text-gray-500 mt-2">{p.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY YOU'LL LOVE IT — feature grid with real datasheet specs */}
        <section className="py-16 bg-gradient-to-br from-[#f6f8f3] via-white to-[#eaf2dc]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-5">
                <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">
                  Why you'll love it
                </span>
                <h2 className="heading-2 text-[#3D5A47] mb-4">
                  Engineered to be safe, silent and seriously efficient.
                </h2>
                <p className="text-gray-700 text-sm mb-6 leading-relaxed">
                  The eCactus Agave SH integrates a hybrid inverter, LFP battery modules, EMS and a built-in fire-suppression kit into one IP65 outdoor-rated cabinet — designed to disappear into the side of your home and quietly power your evenings.
                </p>
                <div className="relative rounded-2xl overflow-hidden shadow-xl border border-[#B5D43B]/20 bg-white p-6">
                  <img
                    src="/product-images/whes/agave-sh-render.png"
                    alt="eCactus Agave SH battery stack"
                    className="w-full h-72 object-contain"
                  />
                  <div
                    className="absolute inset-0 opacity-[0.08] pointer-events-none"
                    style={{
                      backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)',
                      backgroundSize: '20px 20px',
                    }}
                  />
                </div>
              </div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3" data-testid="eofy-features-grid">
                {[
                  { icon: Shield, t: 'LFP (LiFePO4) cells', d: 'Safest mainstream lithium chemistry — thermally stable, long cycle life.' },
                  { icon: Sparkles, t: '100% usable energy', d: '100% depth-of-discharge — every stored kWh is yours to use.' },
                  { icon: Battery, t: 'Modular & scalable', d: 'Stack 1–4 modules per cabinet. Up to 5 cabinets in parallel — grow as you grow.' },
                  { icon: Wrench, t: 'Fast EPS backup', d: '<10 ms switch-over keeps essential circuits running through outages.' },
                  { icon: AlertTriangle, t: 'Built-in fire kit', d: 'Intelligent fire-suppression kit identifies and extinguishes fires within 15 seconds.' },
                  { icon: Info, t: '<25 dB whisper-quiet', d: 'Quieter than a library — install anywhere, even near bedrooms.' },
                  { icon: Calendar, t: '10-year warranty*', d: 'Manufacturer product + performance warranty (70% capacity at year 10).' },
                  { icon: CheckCircle, t: 'IP65 outdoor-rated', d: 'Designed to live outside — dust-tight and weather sealed.' },
                ].map((f, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-gray-100 hover:border-[#B5D43B]/50 hover:shadow-md transition-all">
                    <div className="w-9 h-9 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center mb-2">
                      <f.icon className="w-4 h-4 text-[#3D5A47]" />
                    </div>
                    <h3 className="font-bold text-sm text-[#3D5A47] mb-1">{f.t}</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">{f.d}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Documents block */}
            <div className="mt-10 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                  <h3 className="font-bold text-[#3D5A47]">Official Product Documents</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Hosted by Supa Energy Hub for your convenience.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="eofy-docs">
                {[
                  { href: '/brochures/ecactus/ecactus-agave-sh-datasheet.pdf', t: 'Agave SH Datasheet', s: 'Technical specs · PDF', tid: 'eofy-doc-datasheet' },
                  { href: '/brochures/ecactus/ecactus-agave-sh-user-manual.pdf', t: 'User Manual', s: 'Operation guide · PDF', tid: 'eofy-doc-manual' },
                  { href: '/brochures/ecactus/ecactus-agave-installation-reference.pdf', t: 'Installation Reference', s: 'AU residential BESS · PDF', tid: 'eofy-doc-install' },
                  { href: '/brochures/ecactus/ecactus-aio-warranty-au.pdf', t: 'AU Limited Warranty', s: '10-year warranty · PDF', tid: 'eofy-doc-warranty' },
                ].map((d) => (
                  <a
                    key={d.t}
                    href={d.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-[#B5D43B]/60 hover:shadow-md transition-all bg-white"
                    data-testid={d.tid}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="w-4 h-4 text-[#3D5A47]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#3D5A47] truncate">{d.t}</p>
                      <p className="text-xs text-gray-500">{d.s}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* PRICING TABLE */}
        <section className="py-16 bg-[#f8faf5]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-[#3D5A47] mb-2">EOFY Battery Package Pricing</h2>
            <p className="text-gray-600 mb-6 text-sm">All prices are <em>"from"</em> prices based on standard installation conditions.</p>
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm" data-testid="eofy-pricing-table">
              <table className="w-full text-sm">
                <thead className="bg-[#3D5A47] text-white text-left">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Package</th>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Battery Capacity</th>
                    <th className="px-4 sm:px-6 py-3 font-semibold">EOFY Installed Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 sm:px-6 py-4">eCactus Agave 10.24 kWh Package</td>
                    <td className="px-4 sm:px-6 py-4">10.24 kWh</td>
                    <td className="px-4 sm:px-6 py-4 font-bold text-[#3D5A47]">From $3,999 installed after rebate*</td>
                  </tr>
                  <tr>
                    <td className="px-4 sm:px-6 py-4">eCactus Agave 20.48 kWh Package</td>
                    <td className="px-4 sm:px-6 py-4">20.48 kWh</td>
                    <td className="px-4 sm:px-6 py-4 font-bold text-[#3D5A47]">From $4,999 installed after rebate*</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              *Price assumes standard single-phase installation and customer assignment of the applicable Battery STC rebate to Supa Energy Hub. Final price may vary depending on site conditions.
            </p>
          </div>
        </section>

        {/* WHAT'S INCLUDED */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-[#3D5A47] mb-2">What's Included in the Standard Installation Price?</h2>
            <p className="text-gray-600 mb-6 text-sm">
              The EOFY advertised price is based on a <strong>standard installation</strong>. Here's everything that's covered:
            </p>
            <ul className="grid sm:grid-cols-2 gap-3" data-testid="eofy-included-list">
              {[
                'eCactus Agave hybrid inverter and battery stack',
                'Smart meter supply and installation',
                'Standard delivery',
                'Standard installation',
                'Easy access to battery installation location',
                'Easy access to switchboard',
                'Battery installed within 5 m of the switchboard',
                'Clear and unobstructed cabling pathway',
                'Switchboard has enough space for smart meter and required switches',
                'Existing flat, level, sturdy concrete base / slab available',
                'Suitable fire-resistant wall behind battery location',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 bg-[#f8faf5] rounded-xl p-4">
                  <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ADDITIONAL COSTS */}
        <section className="py-16 bg-[#fdf4ec] border-y border-orange-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Wrench className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="heading-2 text-[#3D5A47] mb-2">Additional Costs That May Apply</h2>
                <p className="text-gray-700 text-sm max-w-3xl">
                  The EOFY advertised price is based on standard installation conditions. Some homes may require extra work depending on site condition, switchboard, battery location, cable run, wall type, and safety requirements.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden border border-orange-100 shadow-sm" data-testid="eofy-extras-table">
              <table className="w-full text-sm">
                <thead className="bg-orange-50 text-[#3D5A47] text-left">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Additional Item</th>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Additional Cost / Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ['Bollards, if required', '$200 per site'],
                    ['Awning, if required', '$200 per site'],
                    ['Additional cabling beyond 5 m', '$25 per metre after the first 5 m'],
                    ['Concrete slab / base, if required', '$150'],
                    ['Fireproof backing / cement sheet, if required', '$150'],
                    ['Additional switchboard works', 'To be estimated before installation'],
                    ['Obstructed cabling pathway', 'Based on additional labour and materials'],
                    ['Any other site-specific works', 'Based on additional labour and materials'],
                  ].map(([k, v]) => (
                    <tr key={k}>
                      <td className="px-4 sm:px-6 py-3 text-gray-700">{k}</td>
                      <td className="px-4 sm:px-6 py-3 font-medium text-[#3D5A47]">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-600 mt-4 italic">
              Some additional costs can only be accurately confirmed after a technician inspects the site, including the switchboard, battery location, cable pathway and installation conditions.
            </p>
          </div>
        </section>

        {/* SITE INSPECTION DEPOSIT */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-[#3D5A47] to-[#1a2820] rounded-3xl p-8 sm:p-12 text-white relative overflow-hidden" data-testid="eofy-deposit-section">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#B5D43B]/10 rounded-full blur-3xl" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#B5D43B] flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-[#1a2820]" />
                  </div>
                  <span className="text-xs uppercase tracking-widest font-bold text-[#B5D43B]">Step required to lock in your deal</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-black mb-4">$250 Site Inspection Deposit</h2>
                <p className="text-white/85 max-w-3xl">
                  To confirm your EOFY battery package, a <strong>$250 non-refundable site inspection deposit</strong> is required before we organise a technician to attend your property.
                </p>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-[#B5D43B] font-bold mb-2">During the site inspection we check:</p>
                    <ul className="space-y-1.5 text-sm text-white/85">
                      {['Battery installation location', 'Switchboard access and available space', 'Cable pathway', 'Distance between switchboard and battery location', 'Wall surface behind the battery', 'Existing base / slab condition', 'Any other site-specific requirements'].map((c) => (
                        <li key={c} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] flex-shrink-0 mt-0.5" /> {c}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-[#B5D43B] font-bold mb-2">What happens next:</p>
                    <ul className="space-y-2 text-sm text-white/85">
                      <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-[#B5D43B] flex-shrink-0 mt-0.5" /><span>If <strong>no extra work</strong> is required, we proceed at the advertised EOFY price.</span></li>
                      <li className="flex items-start gap-2"><Wrench className="w-4 h-4 text-[#B5D43B] flex-shrink-0 mt-0.5" /><span>If <strong>extra work</strong> is needed, we send you an additional cost estimate before booking installation.</span></li>
                      <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] flex-shrink-0 mt-0.5" /><span>If you <strong>proceed</strong>, the $250 is <strong>deducted</strong> from your final balance payable.</span></li>
                      <li className="flex items-start gap-2"><XCircle className="w-4 h-4 text-[#B5D43B] flex-shrink-0 mt-0.5" /><span>If you decide <strong>not to proceed</strong>, the $250 is <strong>retained</strong> as the site inspection / cancellation fee.</span></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 bg-[#f8faf5]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-[#3D5A47] mb-2">How It Works</h2>
            <p className="text-gray-600 mb-8 text-sm">From enquiry to install — here are the seven simple steps:</p>
            <ol className="relative border-l-2 border-[#B5D43B]/30 pl-8 space-y-7" data-testid="eofy-process-steps">
              {[
                { icon: ClipboardList, t: 'Enquire or sign up', d: 'Customer registers interest in the EOFY battery deal via the form below.' },
                { icon: CreditCard, t: 'Pay $250 site inspection deposit', d: 'The deposit is required before a technician is booked.' },
                { icon: Wrench, t: 'Site inspection completed', d: 'Technician checks the switchboard, battery location, cable run, access, wall surface and base/slab.' },
                { icon: Info, t: 'Additional costs confirmed (if any)', d: 'Any extra costs are explained before installation is booked.' },
                { icon: CheckCircle, t: 'Customer confirms final price', d: 'If happy to proceed, the $250 deposit is deducted from the final balance payable.' },
                { icon: Calendar, t: 'Installation is booked', d: 'Installation and material delivery are organised after final confirmation.' },
                { icon: Battery, t: 'Battery is installed', d: 'Installation completed by qualified technicians and electricians.' },
              ].map((s, i) => (
                <li key={i}>
                  <span className="absolute -left-[18px] w-9 h-9 rounded-full bg-[#B5D43B] text-[#1a2820] flex items-center justify-center border-4 border-[#f8faf5] shadow-md">
                    <s.icon className="w-4 h-4" />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#B5D43B]">Step {i + 1}</span>
                  <h3 className="text-lg font-bold text-[#3D5A47] mt-1">{s.t}</h3>
                  <p className="text-sm text-gray-600 mt-1">{s.d}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* CANCELLATION TERMS */}
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl border-l-4 border-[#B5D43B] p-6 sm:p-8 shadow-sm">
              <h2 className="text-xl font-bold text-[#3D5A47] mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#B5D43B]" /> Deposit and Cancellation Terms
              </h2>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2"><span className="text-[#B5D43B] font-bold">•</span> The $250 site inspection deposit is <strong>non-refundable</strong>.</li>
                <li className="flex items-start gap-2"><span className="text-[#B5D43B] font-bold">•</span> If the customer proceeds with installation, the $250 deposit is <strong>deducted from the final balance payable</strong>.</li>
                <li className="flex items-start gap-2"><span className="text-[#B5D43B] font-bold">•</span> If the customer decides not to proceed after the site inspection, or after receiving the additional cost estimate, the $250 deposit is <strong>retained as the site inspection / cancellation fee</strong>.</li>
                <li className="flex items-start gap-2"><span className="text-[#B5D43B] font-bold">•</span> Installation and material delivery will only be organised after the customer accepts the final price, including any additional site-specific costs.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ENQUIRY FORM */}
        <section id="eofy-form" className="py-16 bg-gradient-to-br from-[#3D5A47] to-[#1a2820] text-white scroll-mt-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Claim your EOFY deal</span>
              <h2 className="text-3xl sm:text-4xl font-black">Request My Battery Deal</h2>
              <p className="text-white/80 mt-2 max-w-xl mx-auto text-sm">
                Tell us a little about your home and we'll be in touch to confirm details and explain the $250 site inspection deposit.
              </p>
            </div>

            {status.ok ? (
              <div className="bg-white text-[#3D5A47] rounded-2xl p-8 text-center" data-testid="eofy-form-success">
                <CheckCircle className="w-12 h-12 text-[#B5D43B] mx-auto mb-3" />
                <h3 className="text-xl font-bold mb-2">Thank you.</h3>
                <p className="text-gray-700">
                  Our team will contact you shortly to confirm your details and explain the next steps for the $250 site inspection deposit.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white text-gray-800 rounded-2xl p-6 sm:p-8 space-y-4" data-testid="eofy-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="First Name" required value={form.firstName} onChange={(v) => update('firstName', v)} testid="eofy-first-name" />
                  <Field label="Last Name" required value={form.lastName} onChange={(v) => update('lastName', v)} testid="eofy-last-name" />
                  <Field label="Mobile Number" type="tel" required value={form.mobile} onChange={(v) => update('mobile', v)} testid="eofy-mobile" />
                  <Field label="Email Address" type="email" required value={form.email} onChange={(v) => update('email', v)} testid="eofy-email" />
                  <Field label="Postcode" required value={form.postcode} onChange={(v) => update('postcode', v)} testid="eofy-postcode" />
                  <Select
                    label="Do you currently have solar?"
                    required
                    value={form.hasSolar}
                    onChange={(v) => update('hasSolar', v)}
                    options={[{ v: '', l: 'Select...' }, { v: 'yes', l: 'Yes' }, { v: 'no', l: 'No' }]}
                    testid="eofy-solar"
                  />
                </div>
                <Select
                  label="Which package are you interested in?"
                  required
                  value={form.package}
                  onChange={(v) => update('package', v)}
                  options={[
                    { v: '', l: 'Select package...' },
                    { v: '10.24', l: '10.24 kWh — from $3,999' },
                    { v: '20.48', l: '20.48 kWh — from $4,999' },
                    { v: 'not-sure', l: 'Not sure' },
                  ]}
                  testid="eofy-package"
                />
                <Select
                  label="Preferred contact method"
                  value={form.preferredContact}
                  onChange={(v) => update('preferredContact', v)}
                  options={[
                    { v: 'phone', l: 'Phone' },
                    { v: 'sms', l: 'SMS' },
                    { v: 'email', l: 'Email' },
                  ]}
                  testid="eofy-contact"
                />
                <div>
                  <label className="block text-sm font-semibold text-[#3D5A47] mb-1">Message / Notes</label>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B5D43B]"
                    placeholder="Any specifics about your home or install location"
                    data-testid="eofy-message"
                  />
                </div>
                {status.error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3" data-testid="eofy-form-error">{status.error}</p>
                )}
                <Button
                  type="submit"
                  disabled={status.loading}
                  className="w-full bg-[#B5D43B] hover:bg-[#a3c235] text-[#1a2820] font-bold text-base py-6 rounded-xl gap-2"
                  data-testid="eofy-submit"
                >
                  {status.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {status.loading ? 'Submitting...' : 'Claim EOFY Battery Deal'}
                  {!status.loading && <ArrowRight className="w-4 h-4" />}
                </Button>
                <p className="text-[11px] text-gray-500 text-center">
                  By submitting you agree to be contacted about this EOFY offer. See full disclaimer below.
                </p>
              </form>
            )}
          </div>
        </section>

        {/* DISCLAIMER */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-3">Important Disclaimer</h3>
            <div className="text-xs text-gray-600 space-y-3 leading-relaxed">
              <p>
                EOFY battery package pricing is based on standard single-phase installation conditions and assumes the customer assigns the applicable Battery STC rebate to Supa Energy Hub. Prices shown are "from" prices only. Additional costs may apply depending on site conditions, switchboard requirements, battery location, cable run, wall surface, base/slab condition, safety requirements, access, and other installation factors.
              </p>
              <p>
                Additional costs may include bollards, awning, additional cabling beyond 5 m, switchboard works, concrete slab/base, fireproof backing/cement sheet, obstructed cabling pathway, or other site-specific works.
              </p>
              <p>
                A $250 non-refundable site inspection deposit is required before a technician attends site. If the customer proceeds, this deposit will be deducted from the final balance payable. If the customer decides not to proceed after the site inspection or after receiving any additional cost estimate, the $250 deposit will be retained as the site inspection / cancellation fee.
              </p>
              <p>Final pricing will be confirmed before installation is organised.</p>
            </div>
            <div className="mt-6 text-center">
              <Link to="/contact" className="text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B] inline-flex items-center gap-1">
                Questions? Get in touch <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const Field = ({ label, value, onChange, required, type = 'text', testid }) => (
  <div>
    <label className="block text-sm font-semibold text-[#3D5A47] mb-1">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B5D43B]"
      data-testid={testid}
    />
  </div>
);

const Select = ({ label, value, onChange, options, required, testid }) => (
  <div>
    <label className="block text-sm font-semibold text-[#3D5A47] mb-1">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    <select
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#B5D43B]"
      data-testid={testid}
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>{o.l}</option>
      ))}
    </select>
  </div>
);

export default EOFYBatteryDealPage;
